import {
  type QueryKey,
  type UseSuspenseQueryOptions,
  useMutation,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { toast } from "sonner";
import type { ScopedBinding } from "@/app/types";
import { getEngineApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";
import type {
  AuthProvider,
  AuthProviderUpdateRequest,
  ConnectedAccountItem,
  OAuthProviderItem,
} from "../types";
import { createOAuthProvider } from "./create-o-auth-provider";
import { deleteConnectedAccounts } from "./delete-connected-accounts";
import { deleteOAuthProvider } from "./delete-o-auth-provider";
import { connectedAccountsQueryOptions } from "./get-connected-accounts";
import { oauthProvidersQueryOptions } from "./get-o-auth-providers";
import { connectedAccountsKeys, oauthProvidersKeys } from "./query-keys";
import { updateOAuthProvider } from "./update-o-auth-provider";

export const useGetOAuthProviders = <TData = OAuthProviderItem[]>(
  binding: ScopedBinding = "project",
  options?: Omit<
    UseSuspenseQueryOptions<OAuthProviderItem[], Error, TData, QueryKey>,
    "queryKey" | "queryFn"
  >
) => {
  const scope = useResourceScope();

  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  return useSuspenseQuery<OAuthProviderItem[], Error, TData, QueryKey>({
    ...oauthProvidersQueryOptions({ binding, scope }),
    ...options,
  } as UseSuspenseQueryOptions<OAuthProviderItem[], Error, TData, QueryKey>);
};

export const useCreateOAuthProvider = () => {
  const scope = useResourceScope();

  return useMutation({
    mutationFn: (oauthProvider: AuthProvider) =>
      createOAuthProvider({
        client: getEngineApi(scope, oauthProvider.binding.type || "project"),
        oauthProvider,
      }),
    meta: {
      invalidates: [oauthProvidersKeys.lists({ scope })],
      errorMessage: "Failed to create OAuth provider",
    },
    onSuccess: () => {
      toast.success("OAuth provider created successfully");
    },
  });
};

type UseUpdateOAuthProviderProps = {
  oauthId: string;
  binding?: ScopedBinding;
};
export const useUpdateOAuthProvider = ({
  oauthId,
  binding = "project",
}: UseUpdateOAuthProviderProps) => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (payload: AuthProviderUpdateRequest) =>
      updateOAuthProvider({ client, oauthId, oauthProvider: payload }),
    meta: {
      invalidates: [oauthProvidersKeys.lists({ scope })],
      errorMessage: "Failed to update OAuth provider",
    },
    onSuccess: () => {
      toast.success("OAuth provider updated successfully");
    },
  });
};

export const useDeleteOAuthProvider = () => {
  const scope = useResourceScope();

  return useMutation({
    mutationFn: ({
      oauthId,
      binding,
    }: {
      oauthId: string;
      binding: ScopedBinding;
    }) =>
      deleteOAuthProvider({
        client: getEngineApi(scope, binding),
        oauthId,
      }),
    meta: {
      invalidates: [oauthProvidersKeys.lists({ scope })],
      errorMessage: "Failed to delete OAuth provider",
    },
    onSuccess: () => {
      toast.success("OAuth provider deleted successfully");
    },
  });
};

export const useGetConnectedAccounts = (
  binding: ScopedBinding = "project",
  oauthProviderId?: string,
  options?: Omit<
    UseSuspenseQueryOptions<
      ConnectedAccountItem[],
      Error,
      ConnectedAccountItem[],
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  const scope = useResourceScope();

  return useSuspenseQuery({
    ...connectedAccountsQueryOptions({ binding, oauthProviderId, scope }),
    ...options,
  });
};

export const useDeleteConnectedAccounts = () => {
  const scope = useResourceScope();

  return useMutation({
    mutationFn: ({
      binding = "project",
      connectionId,
    }: {
      binding?: ScopedBinding;
      connectionId: string;
    }) =>
      deleteConnectedAccounts({
        client: getEngineApi(scope, binding),
        connectionId,
      }),
    meta: {
      invalidates: [connectedAccountsKeys.lists({ scope })],
      errorMessage: "Failed to revoke connected user",
    },
    onSuccess: () => {
      toast.success("Connected user revoked successfully");
    },
  });
};
